(() => {
  const HEADER_SELECTOR = '.shopify-section--header .header';
  const BANNER_SELECTOR = '#MainContent > .shopify-section:first-child [allow-transparent-header], [data-xpai-hero-banner][allow-transparent-header], [data-xpai-banner-carousel][allow-transparent-header]';

  function parseColor(value) {
    const text = String(value || '').trim();
    const rgb = text.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?\s*\)/i);
    if (rgb) {
      return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]), a: rgb[4] === undefined ? 1 : Number(rgb[4]) };
    }

    const hex = text.match(/^#([0-9a-f]{3,8})$/i);
    if (!hex) return null;
    let raw = hex[1];
    if (raw.length === 3 || raw.length === 4) raw = raw.split('').map((part) => part + part).join('');
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
      a: raw.length === 8 ? parseInt(raw.slice(6, 8), 16) / 255 : 1
    };
  }

  function luminance(color) {
    if (!color) return null;
    return (color.r * 299 + color.g * 587 + color.b * 114) / 1000;
  }

  function getBannerElement() {
    return document.querySelector(BANNER_SELECTOR);
  }

  function isMenuOpen(header) {
    return Boolean(header.querySelector(
      '.xpai-header-nav-layer .xpai-nav-item:hover, ' +
      '.xpai-header-nav-layer .xpai-nav-item:focus-within, ' +
      '.xpai-header-nav-layer .xpai-nav-disclosure[open]'
    ));
  }

  function isFilled(header) {
    return isMenuOpen(header) ||
      header.classList.contains('is-filled') ||
      window.scrollY > 4;
  }

  function isTransparentAllowed() {
    const banner = getBannerElement();
    return Boolean(banner);
  }

  function getHeaderBaseTextColor(header) {
    const styles = getComputedStyle(header);
    return styles.getPropertyValue('--xpai-web-text-color').trim() ||
      styles.getPropertyValue('--xpai-mobile-text-color').trim() ||
      '#31343b';
  }

  function getBannerTextColor() {
    const banner = getBannerElement();
    if (!banner) return '';

    const activeSlide = banner.querySelector('[data-xpai-hero-slide].is-active, .xpai-banner-carousel__slide.is-active');
    return banner.dataset.xpaiBannerTextColor ||
      activeSlide?.dataset.xpaiTextColor ||
      (activeSlide ? getComputedStyle(activeSlide).getPropertyValue('--xpai-copy-color').trim() : '') ||
      getComputedStyle(banner).color ||
      '';
  }

  function getTone(header, filled) {
    if (filled || !isTransparentAllowed()) {
      return { tone: 'darkText', textColor: getHeaderBaseTextColor(header) };
    }

    const textColor = getBannerTextColor();
    const color = parseColor(textColor);
    const textLuminance = luminance(color);
    return {
      tone: textLuminance !== null && textLuminance >= 166 ? 'lightText' : 'darkText',
      textColor: textColor || getHeaderBaseTextColor(header)
    };
  }

  function setHeaderState(header, state, filled) {
    header.dataset.xpaiTone = state.tone === 'lightText' ? 'light' : 'dark';
    header.dataset.xpaiFilled = filled ? 'true' : 'false';
    header.style.setProperty('--xpai-header-banner-text-color', state.textColor || '');
    header.style.setProperty('--xpai-header-nav-color', state.textColor || getHeaderBaseTextColor(header));
    header.classList.toggle('xpai-header--transparent', !filled);
    header.classList.toggle('xpai-header--filled', filled);
  }

  function removeThemeIcon(action) {
    Array.from(action.children).forEach((child) => {
      if (child.classList.contains('sr-only')) return;
      if (child.classList.contains('xpai-header-action__icon')) return;
      if (child.classList.contains('header__cart-count')) return;
      if (child.tagName.toLowerCase() === 'svg' || child.classList.contains('icon')) child.remove();
    });
  }

  function ensureIcon(action) {
    action.classList.add('xpai-header-action');
    removeThemeIcon(action);

    let shell = action.querySelector('.xpai-header-action__icon');
    if (shell) return shell;

    shell = document.createElement('span');
    shell.className = 'xpai-header-action__icon';
    shell.setAttribute('aria-hidden', 'true');

    const normal = document.createElement('img');
    normal.className = 'xpai-header-action__img xpai-header-action__img--default';
    normal.decoding = 'async';
    normal.loading = 'eager';
    normal.alt = '';

    const hover = document.createElement('img');
    hover.className = 'xpai-header-action__img xpai-header-action__img--active';
    hover.decoding = 'async';
    hover.loading = 'eager';
    hover.alt = '';

    shell.append(normal, hover);
    action.append(shell);
    return shell;
  }

  function setImage(image, source) {
    if (!image) return;
    if (source) {
      if (image.getAttribute('src') !== source) image.setAttribute('src', source);
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.hidden = true;
    }
  }

  function applyCustomIcon(action, tone) {
    const shell = ensureIcon(action);
    const suffix = tone === 'lightText' ? 'Light' : 'Dark';
    const normalSource = action.dataset['xpaiNormal' + suffix] || action.dataset.xpaiNormal || '';
    const hoverSource = action.dataset['xpaiHover' + suffix] || action.dataset.xpaiHover || '';
    const normal = shell.querySelector('.xpai-header-action__img--default');
    const hover = shell.querySelector('.xpai-header-action__img--active');
    const width = Math.max(72, Number(action.dataset.xpaiHoverWidth) || 126);

    action.style.setProperty('--xpai-icon-expanded-width', width + 'px');
    setImage(normal, normalSource);
    setImage(hover, hoverSource);
    action.dataset.xpaiTone = tone;
  }

  async function enhanceHeader() {
    const headers = Array.from(document.querySelectorAll(HEADER_SELECTOR));
    if (!headers.length) return;

    for (const header of headers) {
      const filled = isFilled(header);
      const state = getTone(header, filled);
      setHeaderState(header, state, filled);
      const iconTone = filled ? 'darkText' : state.tone;

      header.querySelectorAll('.header__secondary-nav .header__icon-list[data-xpai-custom-actions]').forEach((iconList) => {
        iconList.querySelectorAll('[data-xpai-custom-action]').forEach((action) => applyCustomIcon(action, iconTone));
      });
    }
  }

  let queued = false;
  function scheduleEnhance() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      enhanceHeader();
    });
  }

  function observeBanner() {
    const banner = getBannerElement();
    if (!banner || banner.dataset.xpaiHeaderObserved === 'true') return;
    banner.dataset.xpaiHeaderObserved = 'true';
    new MutationObserver(scheduleEnhance).observe(banner, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'src', 'data-xpai-banner-text-color']
    });
    banner.querySelectorAll('img').forEach((image) => image.addEventListener('load', scheduleEnhance, { once: true }));
  }

  document.addEventListener('DOMContentLoaded', () => {
    observeBanner();
    scheduleEnhance();
  });
  document.addEventListener('shopify:section:load', () => {
    observeBanner();
    scheduleEnhance();
  });
  document.addEventListener('shopify:section:select', scheduleEnhance);
  document.addEventListener('pointerover', (event) => {
    if (event.target.closest('.header__secondary-nav .header__icon-list, .xpai-header-nav-layer')) scheduleEnhance();
  }, true);
  document.addEventListener('pointerout', (event) => {
    if (event.target.closest('.header__secondary-nav .header__icon-list, .xpai-header-nav-layer')) scheduleEnhance();
  }, true);
  document.addEventListener('focusin', (event) => {
    if (event.target.closest('.header__secondary-nav .header__icon-list, .xpai-header-nav-layer')) scheduleEnhance();
  }, true);
  document.addEventListener('focusout', (event) => {
    if (event.target.closest('.header__secondary-nav .header__icon-list, .xpai-header-nav-layer')) scheduleEnhance();
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('.xpai-header-nav-layer')) scheduleEnhance();
  }, true);
  window.addEventListener('scroll', scheduleEnhance, { passive: true });

  new MutationObserver(scheduleEnhance).observe(document.documentElement, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['class', 'open', 'aria-expanded']
  });

  observeBanner();
  scheduleEnhance();
})();
